
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { Mail, ArrowLeft, ShieldAlert, CheckCircle2 } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    const response = await authService.forgotPassword(email);
    // Info toast as per contract (security reason)
    showToast(response.message || 'If the email exists, a reset link has been sent', 'info');
    setIsSent(true);
    setIsLoading(false);
  };

  if (isSent) {
    return (
      <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500 text-center">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20 animate-pulse">
            <CheckCircle2 size={40} />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">Link Dispatched</h2>
          <p className="text-[11px] text-brand-muted uppercase font-bold tracking-[0.2em] px-4 leading-relaxed">
            Recovery protocols have been sent to <span className="text-white">{email}</span>. Please verify your identity via the link.
          </p>
        </div>
        <Button onClick={() => navigate('/login')} variant="secondary" className="w-full h-14">
          RETURN TO TERMINAL
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-2 animate-in fade-in slide-in-from-left-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-primary/10 rounded-lg">
            <ShieldAlert className="text-brand-primary" size={20} />
          </div>
          <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">Identity Recovery</h2>
        </div>
        <p className="text-[11px] text-brand-muted uppercase font-bold tracking-[0.2em] opacity-60">Initiate Nexus link generation</p>
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-4 delay-100 duration-500">
        <Input
          label="Registered Terminal Email"
          placeholder="identity@coinnexs.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 delay-200 duration-500">
        <Button type="submit" className="w-full h-16" isLoading={isLoading}>
          DISPATCH RECOVERY LINK
        </Button>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full flex items-center justify-center gap-2 text-[10px] text-brand-muted uppercase font-black tracking-widest hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Back to Login
        </button>
      </div>
    </form>
  );
};

export default ForgotPassword;
